import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';

export default function TimeSlotPicker({ lawyerId, officeId, date, selectedTime, onSelectTime }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!lawyerId || !officeId || !date) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    api.get(`/public/slots?lawyerId=${lawyerId}&officeId=${officeId}&date=${date}`)
      .then((res) => {
        if (isMounted) {
          if (res?.success) {
            setData(res);
          } else {
            setError(res?.message || 'Vaqtlarni yuklashda xatolik yuz berdi');
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message || 'Server bilan bog‘lanishda xatolik');
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [lawyerId, officeId, date]);

  if (!date) {
    return (
      <div className="p-8 text-center bg-slate-50 border border-dashed border-slate-200 rounded-lg text-slate-500 text-sm">
        <Clock className="w-6 h-6 mx-auto mb-2 text-slate-400" />
        Vaqt oraliqlarini ko‘rish uchun avval yuqoridan qulay sanani tanlang.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="text-xs text-slate-500 animate-pulse">Bo‘sh vaqtlar hisoblanmoqda...</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="h-10 bg-slate-200 animate-pulse rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-xs flex items-center">
        <AlertCircle className="w-4 h-4 mr-2 shrink-0" />
        {error}
      </div>
    );
  }

  if (!data?.isOpen) {
    const cleanReason = data?.reason
      ? data.reason.replace(/\s*\([^\)]*qabul[^\)]*\)/gi, '').replace(/\s*—\s*qabul\s*yo[‘'`]?q/gi, '').trim()
      : 'Dam olish kuni';
    return (
      <div className="p-6 text-center bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
        <AlertCircle className="w-6 h-6 mx-auto mb-2 text-amber-600" />
        <strong>Qabul mavjud emas:</strong> {cleanReason}
      </div>
    );
  }

  const slots = data?.slots || [];
  const availableSlots = slots.filter((s) => s.available);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-500 gap-1.5">
        <div>
          <span className="font-semibold text-slate-700">Ish vaqti:</span> {data.workingHours}
          {data.breakTime && ` (Tushlik: ${data.breakTime})`}
        </div>
        <div className="text-emerald-700 font-medium">
          {availableSlots.length} ta bo‘sh oraliq mavjud
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 sm:gap-2.5">
        {slots.map((slot) => {
          const isSelected = selectedTime === slot.time;
          return (
            <button
              key={slot.time}
              type="button"
              disabled={!slot.available}
              onClick={() => onSelectTime(slot.time)}
              title={!slot.available ? slot.reason : `${slot.time} - ${slot.endTime}`}
              className={`min-h-[42px] py-2 px-1 text-center rounded border text-xs font-semibold transition-all touch-manipulation cursor-pointer ${
                isSelected
                  ? 'bg-[#091322] text-[#c5a880] border-[#c5a880] ring-2 ring-[#c5a880]/30 shadow-sm'
                  : slot.available
                  ? 'bg-white hover:bg-slate-50 text-slate-800 border-slate-300 hover:border-[#c5a880]'
                  : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through'
              }`}
            >
              {slot.time}
            </button>
          );
        })}
      </div>
    </div>
  );
}
